<template>
  <!-- eslint-disable vue/max-attributes-per-line -->
  <div>
    <my-loader :loader="loader" />

    <v-breadcrumbs :items="breadcrumbs" divider="/" class="pa-0 mb-4">
      <template #item="{ item }">
        <v-breadcrumbs-item :to="item.to" :disabled="item.disabled" exact>
          {{ item.text }}
        </v-breadcrumbs-item>
      </template>
    </v-breadcrumbs>

    <div class="d-flex align-start mb-6 flex-wrap" style="gap: 12px">
      <div class="flex-grow-1">
        <p class="text-2xl mb-1">
          Тренеры · кэшбэк
        </p>
        <p class="text--secondary mb-0">
          {{ sectionSubtitle }}
        </p>
      </div>
    </div>

    <v-tabs class="mb-5">
      <v-tab to="/coaches/list" exact>
        Список
      </v-tab>
      <v-tab to="/coaches/topups" exact>
        Начисления
      </v-tab>
      <v-tab to="/coaches/settings" exact>
        Статусы
      </v-tab>
      <v-tab to="/coaches/periods" exact>
        Периоды
      </v-tab>
    </v-tabs>

    <v-card v-if="sectionSwitchLoading" flat outlined class="mb-5 pa-4">
      <v-progress-linear indeterminate color="primary" class="mb-4" />
      <v-skeleton-loader type="table" />
    </v-card>

    <!-- ========== LIST ========== -->
    <div v-show="section === 'list' && !sectionSwitchLoading">
      <v-card flat outlined class="mb-4 pa-4">
        <v-row dense>
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="listFilters.search"
              label="Поиск по ФИО"
              dense
              outlined
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
            />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="listFilters.sport"
              :items="sportFilterItems"
              item-text="text"
              item-value="value"
              label="Спорт"
              dense
              outlined
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="listFilters.contract"
              :items="contractFilterItems"
              item-text="text"
              item-value="value"
              label="Контракт"
              dense
              outlined
              hide-details
            />
          </v-col>
        </v-row>
      </v-card>

      <v-card>
        <v-data-table
          :headers="listHeaders"
          :items="filteredCoaches"
          :loading="listLoading"
          loading-text="Загрузка тренеров…"
          item-key="_key"
          :items-per-page="50"
          class="elevation-0"
        >
          <template #[`item.full_name`]="{ item }">
            <template v-if="item.has_profile">
              <a
                href="#"
                class="font-weight-medium text-decoration-none"
                @click.prevent="openTrainingsDialog(item)"
              >
                {{ item.full_name }}
              </a>
            </template>
            <span v-else class="text--disabled">
              {{ item.full_name }}
              <span class="text-caption d-block">нет профиля</span>
            </span>
          </template>

          <template #[`item.sport_display`]="{ item }">
            <span v-if="item.has_profile">{{ item.sport_display || sportLabel(item.sport) }}</span>
            <span v-else class="text--disabled">—</span>
          </template>

          <template #[`item.has_contract`]="{ item }">
            <v-switch
              v-if="item.has_profile"
              :key="`contract-${item.coach_id}-${item.has_contract}-${item._contractRev || 0}`"
              :input-value="item.has_contract"
              :disabled="contractSavingId === item.coach_id"
              dense
              hide-details
              class="mt-0 pt-0"
              @change="onContractToggle(item, $event)"
            />
            <span v-else class="text--disabled">—</span>
          </template>

          <template #[`item.hours`]="{ item }">
            <span v-if="item.has_profile">{{ formatNumber(item.hours) }}</span>
            <span v-else class="text--disabled">—</span>
          </template>

          <template #[`item.status_name`]="{ item }">
            <v-chip
              v-if="item.has_profile && item.status_name"
              small
              outlined
              label
            >
              {{ item.status_name }}
            </v-chip>
            <span v-else class="text--disabled">—</span>
          </template>

          <template #[`item.percent`]="{ item }">
            <span v-if="item.has_profile && item.percent != null">
              {{ formatPercent(item.percent) }}%
            </span>
            <span v-else class="text--disabled">—</span>
          </template>

          <template #[`item.cashback`]="{ item }">
            <span v-if="item.has_profile">{{ formatMoney(item.cashback) }}</span>
            <span v-else class="text--disabled">—</span>
          </template>

          <template #[`item.credited_cashback`]="{ item }">
            <span v-if="item.has_profile">{{ formatMoney(item.credited_cashback) }}</span>
            <span v-else class="text--disabled">—</span>
          </template>

          <template #[`item.next_hint`]="{ item }">
            <span
              v-if="item.has_profile && item.next_status_name"
              class="text-caption"
            >
              ещё {{ item.hours_to_next_status }} ч →
              {{ item.next_status_name }}
              <template v-if="item.next_status_percent != null">
                ({{ formatPercent(item.next_status_percent) }}%)
              </template>
            </span>
            <span v-else class="text--disabled">—</span>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- ========== TOPUPS ========== -->
    <div v-show="section === 'topups' && !sectionSwitchLoading">
      <v-card flat outlined class="mb-4 pa-4">
        <v-row dense>
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="topupsFilters.search"
              label="Поиск по ФИО"
              dense
              outlined
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
            />
          </v-col>
          <v-col cols="12" sm="4" md="2">
            <v-select
              v-model="topupsFilters.year"
              :items="topupsYearItems"
              label="Год"
              dense
              outlined
              hide-details
              clearable
              @change="onTopupsYearChange"
            />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="topupsFilters.period_id"
              :items="topupsPeriodItems"
              item-text="text"
              item-value="value"
              label="Период"
              dense
              outlined
              hide-details
              clearable
              :loading="topupsPeriodsLoading"
              @change="loadTopups"
            />
          </v-col>
          <v-col cols="12" sm="12" md="4" class="d-flex align-center justify-end" style="gap: 8px">
            <v-btn color="primary" text :loading="topupsLoading" @click="loadTopups">
              Обновить
            </v-btn>
            <v-btn color="primary" @click="startTopupWizard">
              Начислить
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <v-card>
        <v-data-table
          :headers="topupsHeaders"
          :items="filteredTopups"
          :loading="topupsLoading"
          loading-text="Загрузка начислений…"
          :server-items-length="topupsTotal"
          :options.sync="topupsTableOptions"
          :footer-props="{
            'items-per-page-options': [20, 50, 100],
          }"
          class="elevation-0"
          @update:options="onTopupsOptions"
        >
          <template #[`item.id`]="{ item }">
            <v-btn text small color="primary" @click="openTopupDetail(item)">
              {{ item.id }}
            </v-btn>
          </template>

          <template #[`item.period`]="{ item }">
            <div>
              <div class="font-weight-medium">
                {{ (item.period && item.period.name) || `№${item.period_quarter}` }}
              </div>
              <div class="text-caption text--secondary">
                {{ item.period_year }}
                <template v-if="item.period">
                  · {{ item.period.date_from }} — {{ item.period.date_to }}
                </template>
              </div>
            </div>
          </template>

          <template #[`item.hours_total`]="{ item }">
            {{ formatHours(item.hours_total) }}
          </template>

          <template #[`item.total_percent`]="{ item }">
            {{ formatPercent(item.total_percent) }}%
          </template>

          <template #[`item.payments_total`]="{ item }">
            {{ formatMoney(item.payments_total) }}
          </template>

          <template #[`item.cashback_amount`]="{ item }">
            <span class="font-weight-medium">
              {{ formatMoney(item.cashback_amount) }}
            </span>
          </template>

          <template #[`item.paid_at`]="{ item }">
            <v-chip
              v-if="item.paid_at"
              small
              color="success"
              text-color="white"
            >
              {{ formatDateTime(item.paid_at) }}
            </v-chip>
            <v-chip v-else small color="warning" text-color="white">
              не выплачено
            </v-chip>
          </template>

          <template #[`item.created_at`]="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- ========== SETTINGS ========== -->
    <div v-show="section === 'settings' && !sectionSwitchLoading">
      <v-card flat outlined class="mb-4">
        <v-toolbar flat dense>
          <v-toolbar-title class="text-subtitle-1">
            Статусы кэшбэка
          </v-toolbar-title>
          <v-spacer />
          <v-btn small text color="primary" class="mr-2" @click="addStatusRow">
            <v-icon left small>
              {{ mdiPlus }}
            </v-icon>
            Добавить
          </v-btn>
          <v-btn
            small
            color="primary"
            :loading="statusesSaving"
            @click="saveStatuses"
          >
            Сохранить
          </v-btn>
        </v-toolbar>

        <v-data-table
          :headers="statusHeaders"
          :items="statusRows"
          :loading="statusesLoading"
          hide-default-footer
          :items-per-page="-1"
          class="elevation-0"
        >
          <template #[`item.sport`]="{ item }">
            <v-select
              v-model="item.sport"
              :items="sportItems"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              class="my-1"
              style="max-width: 180px"
            />
          </template>

          <template #[`item.name`]="{ item }">
            <v-text-field
              v-model="item.name"
              dense
              outlined
              hide-details
              class="my-1"
            />
          </template>

          <template #[`item.hours_from`]="{ item }">
            <v-text-field
              v-model.number="item.hours_from"
              type="number"
              dense
              outlined
              hide-details
              class="my-1"
              style="max-width: 110px"
            />
          </template>

          <template #[`item.hours_to`]="{ item }">
            <v-text-field
              v-model="item.hours_to"
              type="number"
              dense
              outlined
              hide-details
              clearable
              class="my-1"
              style="max-width: 110px"
              @change="normalizeHoursTo(item)"
            />
          </template>

          <template #[`item.contract_cashback_percent`]="{ item }">
            <v-text-field
              v-model="item.contract_cashback_percent"
              dense
              outlined
              hide-details
              class="my-1"
              style="max-width: 100px"
              suffix="%"
            />
          </template>

          <template #[`item.production_cashback_percent`]="{ item }">
            <v-text-field
              v-model="item.production_cashback_percent"
              dense
              outlined
              hide-details
              class="my-1"
              style="max-width: 100px"
              suffix="%"
            />
          </template>

          <template #[`item.total_percent`]="{ item }">
            <span class="font-weight-medium">
              {{ formatPercent(rowTotalPercent(item)) }}%
            </span>
          </template>

          <template #[`item.actions`]="{ item, index }">
            <v-btn icon small color="error" @click="removeStatusRow(index)">
              <v-icon small>
                {{ mdiDelete }}
              </v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- ========== PERIODS ========== -->
    <div v-show="section === 'periods' && !sectionSwitchLoading">
      <v-card flat outlined class="mb-4 pa-4">
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="periodsYear"
              :items="yearItems"
              label="Год"
              dense
              outlined
              hide-details
              @change="loadPeriods"
            />
          </v-col>
          <v-col cols="12" sm="8" md="9" class="d-flex align-center flex-wrap" style="gap: 8px">
            <v-alert
              v-if="periodsCoverageHint"
              dense
              text
              :type="periodsCoverageOk ? 'success' : 'warning'"
              class="mb-0 flex-grow-1"
            >
              {{ periodsCoverageHint }}
            </v-alert>
            <v-spacer />
            <v-btn small text color="primary" @click="addPeriodRow">
              <v-icon left small>
                {{ mdiPlus }}
              </v-icon>
              Добавить период
            </v-btn>
            <v-btn
              small
              color="primary"
              :loading="periodsSaving"
              @click="savePeriods"
            >
              Сохранить
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <v-card>
        <v-data-table
          :headers="periodHeaders"
          :items="periodRows"
          :loading="periodsLoading"
          hide-default-footer
          :items-per-page="-1"
          class="elevation-0"
        >
          <template #[`item.name`]="{ item }">
            <v-text-field
              v-model="item.name"
              dense
              outlined
              hide-details
              class="my-1"
            />
          </template>

          <template #[`item.date_from`]="{ item }">
            <v-text-field
              v-model="item.date_from"
              type="date"
              dense
              outlined
              hide-details
              class="my-1"
              :disabled="isPeriodLocked(item)"
            />
          </template>

          <template #[`item.date_to`]="{ item }">
            <v-text-field
              v-model="item.date_to"
              type="date"
              dense
              outlined
              hide-details
              class="my-1"
              :disabled="isPeriodLocked(item)"
            />
          </template>

          <template #[`item.locked`]="{ item }">
            <v-chip v-if="isPeriodLocked(item)" x-small color="warning" label>
              есть начисления
            </v-chip>
            <span v-else class="text--disabled">—</span>
          </template>

          <template #[`item.actions`]="{ item, index }">
            <v-btn
              icon
              small
              color="error"
              :disabled="isPeriodLocked(item)"
              @click="removePeriodRow(index)"
            >
              <v-icon small>
                {{ mdiDelete }}
              </v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- Trainings dialog -->
    <v-dialog v-model="trainingsDialog" max-width="960" scrollable>
      <v-card>
        <v-toolbar flat color="primary" dark dense>
          <v-toolbar-title>
            Тренировки —
            {{ trainingsCoachName }}
            <span v-if="trainingsPeriodLabel" class="text-caption font-weight-regular">
              · {{ trainingsPeriodLabel }}
            </span>
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon dark @click="trainingsDialog = false">
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-progress-linear
            v-if="trainingsLoading"
            indeterminate
            color="primary"
            class="mb-4"
          />
          <v-data-table
            v-else
            :headers="trainingsHeaders"
            :items="trainings"
            :items-per-page="25"
            dense
            class="elevation-0"
          >
            <template #[`item.paid_amount`]="{ item }">
              {{ formatMoney(item.paid_amount) }}
            </template>
            <template #[`item.cashback_amount`]="{ item }">
              {{ formatMoney(item.cashback_amount) }}
            </template>
            <template #[`item.duration_hours`]="{ item }">
              {{ item.duration_hours }}
            </template>
            <template #[`item.paid_by_abonement`]="{ item }">
              {{ item.paid_by_abonement ? 'да' : 'нет' }}
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="trainingsDialog = false">
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Topup wizard -->
    <v-dialog
      v-model="topupDialog"
      :max-width="topupStep === 1 ? 520 : 1100"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar flat color="primary" dark dense>
          <v-toolbar-title>
            <template v-if="topupStep === 1">
              Начисление · шаг 1: период
            </template>
            <template v-else>
              Начисление · шаг 2: проверка
            </template>
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            dark
            :disabled="topupSaving || topupPreviewLoading"
            @click="closeTopupWizard"
          >
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text v-if="topupStep === 1" class="pt-5">
          <p class="text-caption text--secondary mb-3">
            Начисление за период доступно только после начала следующего периода.
            Будут взяты все тренеры с подписанным контрактом.
          </p>
          <v-select
            v-model="topupPeriodId"
            :items="finishedPeriodItems"
            item-text="text"
            item-value="value"
            label="Период"
            dense
            outlined
            :loading="topupPeriodsLoading"
            :hint="finishedPeriodItems.length ? '' : 'Нет периодов, для которых уже начался следующий'"
            persistent-hint
          />
          <v-textarea
            v-model="topupComment"
            label="Комментарий"
            dense
            outlined
            rows="2"
            hide-details
            class="mt-3"
          />
        </v-card-text>

        <v-card-text v-else class="pt-5">
          <div class="d-flex flex-wrap align-center mb-3" style="gap: 12px">
            <div>
              <div class="text-caption text--secondary">Период</div>
              <div class="font-weight-medium">
                {{ topupPeriodLabel }}
              </div>
            </div>
            <v-spacer />
            <div class="text-body-2">
              К начислению:
              <strong>{{ topupEligibleSelectedIds.length }}</strong>
              из {{ topupPreviewRows.length }}
              · сумма
              <strong>{{ formatMoney(topupPreviewSelectedTotal) }}</strong>
            </div>
          </div>

          <v-alert
            v-if="topupPreviewError"
            dense
            text
            type="error"
            class="mb-3"
          >
            {{ topupPreviewError }}
          </v-alert>

          <v-data-table
            v-model="topupPreviewSelected"
            :headers="topupPreviewHeaders"
            :items="topupPreviewRows"
            :loading="topupPreviewLoading"
            loading-text="Считаем кэшбэк по тренерам…"
            item-key="coach_id"
            show-select
            hide-default-footer
            :items-per-page="-1"
            dense
            class="elevation-0"
            @item-selected="onTopupPreviewItemSelected"
            @toggle-select-all="onTopupPreviewToggleAll"
          >
            <template #[`item.full_name`]="{ item }">
              <a
                href="#"
                class="font-weight-medium text-decoration-none"
                @click.prevent="openTrainingsDialog(item)"
              >
                {{ item.full_name }}
              </a>
            </template>

            <template #[`item.status_name`]="{ item }">
              <v-chip v-if="item.status_name" small outlined label>
                {{ item.status_name }}
              </v-chip>
              <span v-else class="text--disabled">—</span>
            </template>

            <template #[`item.percent`]="{ item }">
              <span v-if="item.percent != null">
                {{ formatPercent(item.percent) }}%
              </span>
              <span v-else class="text--disabled">—</span>
            </template>

            <template #[`item.cashback`]="{ item }">
              {{ formatMoney(item.cashback) }}
            </template>

            <template #[`item.credited_cashback`]="{ item }">
              <v-chip
                v-if="item.already_accrued"
                small
                color="success"
                text-color="white"
              >
                {{ formatMoney(item.credited_cashback) }}
              </v-chip>
              <span v-else class="text--disabled">—</span>
            </template>

            <template #[`item.block_reason`]="{ item }">
              <span v-if="item.already_accrued" class="text-caption success--text">
                уже начислено
              </span>
              <span v-else-if="item.block_reason" class="text-caption error--text">
                {{ item.block_reason }}
              </span>
              <span v-else class="text--disabled">—</span>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-btn
            v-if="topupStep === 2"
            text
            :disabled="topupSaving || topupPreviewLoading"
            @click="topupStep = 1"
          >
            Назад
          </v-btn>
          <v-spacer />
          <v-btn
            text
            :disabled="topupSaving || topupPreviewLoading"
            @click="closeTopupWizard"
          >
            Отмена
          </v-btn>
          <v-btn
            v-if="topupStep === 1"
            color="primary"
            :disabled="!topupPeriodId"
            :loading="topupPreviewLoading"
            @click="loadTopupPreview"
          >
            Далее
          </v-btn>
          <v-btn
            v-else
            color="primary"
            :loading="topupSaving"
            :disabled="!topupEligibleSelectedIds.length || topupPreviewLoading"
            @click="submitTopup"
          >
            Начислить ({{ topupEligibleSelectedIds.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Topup detail dialog -->
    <v-dialog v-model="topupDetailDialog" max-width="960">
      <v-card>
        <v-toolbar flat color="primary" dark dense>
          <v-toolbar-title>
            Начисление #{{ topupDetail && topupDetail.id }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon dark @click="topupDetailDialog = false">
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-5">
          <div v-if="topupDetailLoading" class="py-6 text-center">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <template v-else-if="topupDetail">
            <v-row dense class="mb-4">
              <v-col cols="12" sm="6" md="3">
                <div class="text-caption text--secondary">Тренер</div>
                <div class="font-weight-medium">
                  {{ (topupDetail.coach && topupDetail.coach.full_name) || '—' }}
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="text-caption text--secondary">Период</div>
                <div class="font-weight-medium">
                  {{ (topupDetail.period && topupDetail.period.name) || '—' }}
                </div>
                <div class="text-caption text--secondary">
                  <template v-if="topupDetail.period">
                    {{ topupDetail.period.date_from }} — {{ topupDetail.period.date_to }}
                  </template>
                </div>
              </v-col>
              <v-col cols="6" sm="3" md="2">
                <div class="text-caption text--secondary">Часы</div>
                <div class="font-weight-medium">
                  {{ formatHours(topupDetail.hours_total) }}
                </div>
              </v-col>
              <v-col cols="6" sm="3" md="2">
                <div class="text-caption text--secondary">%</div>
                <div class="font-weight-medium">
                  {{ formatPercent(topupDetail.total_percent) }}%
                </div>
              </v-col>
              <v-col cols="6" sm="3" md="2">
                <div class="text-caption text--secondary">Кэшбэк</div>
                <div class="font-weight-medium">
                  {{ formatMoney(topupDetail.cashback_amount) }}
                </div>
              </v-col>
            </v-row>

            <v-alert
              v-if="topupDetail.paid_at"
              dense
              text
              type="success"
              class="mb-4"
            >
              Выплачено {{ formatDateTime(topupDetail.paid_at) }}
            </v-alert>
            <v-alert v-else dense text type="warning" class="mb-4">
              Начисление создано, выплата в YClients не выполнена
            </v-alert>

            <p v-if="topupDetail.comment" class="mb-3 text-body-2">
              Комментарий: {{ topupDetail.comment }}
            </p>

            <v-data-table
              :headers="topupDetailItemHeaders"
              :items="topupDetail.items || []"
              hide-default-footer
              :items-per-page="-1"
              dense
              class="elevation-0"
            >
              <template #[`item.training_date`]="{ item }">
                {{ item.training_date || '—' }}
              </template>
              <template #[`item.time`]="{ item }">
                {{ formatTimeRange(item.start_time, item.end_time) }}
              </template>
              <template #[`item.paid_amount`]="{ item }">
                {{ formatMoney(item.paid_amount) }}
              </template>
              <template #[`item.cashback_amount`]="{ item }">
                {{ formatMoney(item.cashback_amount) }}
              </template>
            </v-data-table>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="topupDetailDialog = false">
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="5000">
      {{ snackbarText }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          OK
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mdiPlus, mdiDelete, mdiClose } from '@mdi/js'
import Api from '@/api/api'
import MyLoader from '@/components/MyLoader.vue'

const SPORT_LABELS = {
  BADMINTON: 'Бадминтон',
  SQUASH: 'Сквош',
  TABLE_TENNIS: 'Настольный теннис',
}

function todayIso() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')

  return `${y}-${m}-${day}`
}

function parseMoney(value) {
  if (value == null || value === '') return null
  const n = Number(value)

  return Number.isFinite(n) ? n : null
}

export default {
  name: 'Coaches',
  components: { MyLoader },
  data() {
    const currentYear = new Date().getFullYear()

    return {
      mdiPlus,
      mdiDelete,
      mdiClose,

      loader: false,
      sectionSwitchLoading: false,

      snackbar: false,
      snackbarText: '',
      snackbarColor: 'primary',

      // list
      listLoading: false,
      coachesRaw: [],
      contractSavingId: null,
      listFilters: {
        search: '',
        sport: null,
        contract: 'all',
      },
      sportFilterItems: [
        { text: 'Бадминтон', value: 'BADMINTON' },
        { text: 'Сквош', value: 'SQUASH' },
        { text: 'Настольный теннис', value: 'TABLE_TENNIS' },
      ],
      contractFilterItems: [
        { text: 'Все', value: 'all' },
        { text: 'С контрактом', value: 'yes' },
        { text: 'Без контракта', value: 'no' },
      ],
      listHeaders: [
        { text: 'ФИО', value: 'full_name' },
        { text: 'Спорт', value: 'sport_display', width: 140 },
        { text: 'Контракт', value: 'has_contract', sortable: false, width: 100 },
        { text: 'Часы', value: 'hours', width: 80 },
        { text: 'Статус', value: 'status_name', width: 120 },
        { text: '%', value: 'percent', width: 80 },
        { text: 'Оценка ₽', value: 'cashback', width: 110 },
        { text: 'Начислено ₽', value: 'credited_cashback', width: 120 },
        { text: 'До след. статуса', value: 'next_hint', sortable: false },
      ],

      // trainings dialog
      trainingsDialog: false,
      trainingsLoading: false,
      trainings: [],
      trainingsCoachName: '',
      trainingsPeriodLabel: '',
      trainingsHeaders: [
        { text: 'Дата', value: 'training_date', width: 110 },
        { text: 'Начало', value: 'start_time', width: 90 },
        { text: 'Конец', value: 'end_time', width: 90 },
        { text: 'Клиент', value: 'client_name' },
        { text: 'Корт', value: 'court_number', width: 70 },
        { text: 'Часы', value: 'duration_hours', width: 70 },
        { text: 'Оплата ₽', value: 'paid_amount', width: 100 },
        { text: 'Абонемент', value: 'paid_by_abonement', width: 100 },
        { text: 'Кэшбэк ₽', value: 'cashback_amount', width: 100 },
      ],

      // topup wizard
      topupDialog: false,
      topupStep: 1,
      topupPeriodsLoading: false,
      topupPreviewLoading: false,
      topupSaving: false,
      topupPeriodId: null,
      topupComment: '',
      finishedPeriodItems: [],
      topupPreviewRows: [],
      topupPreviewSelected: [],
      topupPreviewError: '',
      topupPreviewHeaders: [
        { text: 'ФИО', value: 'full_name' },
        { text: 'Спорт', value: 'sport_display', width: 130 },
        { text: 'Часы', value: 'hours', width: 80 },
        { text: 'Статус', value: 'status_name', width: 120 },
        { text: '%', value: 'percent', width: 70 },
        { text: 'К начислению ₽', value: 'cashback', width: 130 },
        { text: 'Уже начислено', value: 'credited_cashback', width: 130 },
        { text: 'Примечание', value: 'block_reason', sortable: false },
      ],

      // topups list
      topupsLoading: false,
      topupsPeriodsLoading: false,
      topupsRows: [],
      topupsTotal: 0,
      topupsFilters: {
        search: '',
        year: currentYear,
        period_id: null,
      },
      topupsPeriodItems: [],
      topupsTableOptions: {
        page: 1,
        itemsPerPage: 20,
        sortBy: [],
        sortDesc: [],
      },
      topupsHeaders: [
        { text: 'ID', value: 'id', width: 80 },
        { text: 'Тренер', value: 'user_name' },
        { text: 'Период', value: 'period', sortable: false },
        { text: 'Спорт', value: 'sport', width: 120 },
        { text: 'Часы', value: 'hours_total', width: 80 },
        { text: '%', value: 'total_percent', width: 80 },
        { text: 'Оплаты ₽', value: 'payments_total', width: 110 },
        { text: 'Кэшбэк ₽', value: 'cashback_amount', width: 110 },
        { text: 'Выплата', value: 'paid_at', width: 160 },
        { text: 'Создано', value: 'created_at', width: 150 },
      ],
      topupDetailDialog: false,
      topupDetailLoading: false,
      topupDetail: null,
      topupDetailItemHeaders: [
        { text: 'Дата', value: 'training_date', width: 110 },
        { text: 'Время', value: 'time', sortable: false, width: 120 },
        { text: 'Клиент', value: 'client_name' },
        { text: 'Корт', value: 'court_number', width: 70 },
        { text: 'Часы', value: 'duration_hours', width: 70 },
        { text: 'Оплата ₽', value: 'paid_amount', width: 100 },
        { text: 'Кэшбэк ₽', value: 'cashback_amount', width: 100 },
      ],

      // settings
      statusesLoading: false,
      statusesSaving: false,
      statusRows: [],
      sportItems: [
        { text: 'Бадминтон', value: 'BADMINTON' },
        { text: 'Сквош', value: 'SQUASH' },
        { text: 'Настольный теннис', value: 'TABLE_TENNIS' },
      ],
      statusHeaders: [
        { text: 'Спорт', value: 'sport', width: 180 },
        { text: 'Название', value: 'name' },
        { text: 'Часы от', value: 'hours_from', width: 120 },
        { text: 'Часы до', value: 'hours_to', width: 120 },
        { text: 'Контракт %', value: 'contract_cashback_percent', width: 120 },
        { text: 'Выработка %', value: 'production_cashback_percent', width: 120 },
        { text: 'Итого %', value: 'total_percent', width: 90, sortable: false },
        { text: '', value: 'actions', sortable: false, width: 56 },
      ],

      // periods
      periodsLoading: false,
      periodsSaving: false,
      periodsYear: currentYear,
      periodRows: [],
      hasTopups: {},
      periodHeaders: [
        { text: 'Название', value: 'name' },
        { text: 'Дата с', value: 'date_from', width: 180 },
        { text: 'Дата по', value: 'date_to', width: 180 },
        { text: 'Статус', value: 'locked', sortable: false, width: 140 },
        { text: '', value: 'actions', sortable: false, width: 56 },
      ],
    }
  },
  computed: {
    section() {
      return this.$route.params.section || 'list'
    },
    sectionSubtitle() {
      const map = {
        list: 'Список тренеров, контракты и данные текущего периода.',
        topups: 'Начисление кэшбэка и история начислений.',
        settings: 'Настройка статусов и процентов кэшбэка.',
        periods: 'Расчётные периоды по годам.',
      }

      return map[this.section] || ''
    },
    breadcrumbs() {
      const titles = {
        list: 'Список',
        topups: 'Начисления',
        settings: 'Статусы',
        periods: 'Периоды',
      }

      return [
        { text: 'Тренеры · кэшбэк', to: '/coaches/list', disabled: false },
        { text: titles[this.section] || 'Список', disabled: true },
      ]
    },
    topupsYearItems() {
      const y = new Date().getFullYear()
      const minYear = 2026
      const years = []
      for (let year = Math.max(minYear, y + 1); year >= minYear; year -= 1) {
        years.push(year)
      }

      return years
    },
    filteredTopups() {
      const search = (this.topupsFilters.search || '').trim().toLowerCase()
      if (!search) return this.topupsRows

      return this.topupsRows.filter(row => {
        const name = (row.user_name || '').toLowerCase()

        return name.includes(search)
      })
    },
    yearItems() {
      const y = new Date().getFullYear()
      const minYear = 2026
      const years = []
      for (let year = minYear; year <= y + 1; year += 1) {
        years.push(year)
      }

      return years
    },
    coachesFlat() {
      return (this.coachesRaw || []).map((row, index) => {
        const user = row.user || {}
        const coach = row.coach
        const summary = row.summary || {}
        const current = summary.current_period || null
        const status = (current && current.status) || null
        const hasProfile = !!coach

        return {
          _key: hasProfile ? `coach-${coach.id}` : `user-${user.id || index}`,
          has_profile: hasProfile,
          coach_id: hasProfile ? coach.id : null,
          user_id: user.id,
          full_name: (user.full_name || (coach && coach.full_name) || '—').trim() || '—',
          sport: hasProfile ? coach.sport : null,
          sport_display: hasProfile ? (coach.sport_display || SPORT_LABELS[coach.sport] || coach.sport) : null,
          has_contract: hasProfile ? !!coach.contract : false,
          contract_date: hasProfile ? coach.contract_date : null,
          hours: current ? current.hours : null,
          status_name: status ? status.status_name : null,
          percent: status ? status.total_percent : null,
          cashback: current ? current.cashback : null,
          credited_cashback: current ? current.credited_cashback : null,
          hours_to_next_status: current ? current.hours_to_next_status : null,
          next_status_name: current ? current.next_status_name : null,
          next_status_percent: current ? current.next_status_percent : null,
          period_id: current && current.period ? current.period.id : null,
          period: current ? current.period : null,
        }
      })
    },
    filteredCoaches() {
      const search = (this.listFilters.search || '').trim().toLowerCase()
      const sport = this.listFilters.sport
      const contract = this.listFilters.contract

      return this.coachesFlat.filter(item => {
        if (search) {
          const hay = `${item.full_name} ${item.user_id || ''}`.toLowerCase()
          if (!hay.includes(search)) return false
        }
        if (sport && item.sport !== sport) return false
        if (contract === 'yes' && (!item.has_profile || !item.has_contract)) return false
        if (contract === 'no' && item.has_profile && item.has_contract) return false

        return true
      })
    },
    topupPeriodLabel() {
      const item = this.finishedPeriodItems.find(
        p => p.value === this.topupPeriodId,
      )

      return item ? item.text : ''
    },
    topupEligibleRows() {
      return (this.topupPreviewRows || []).filter(
        row => row && !row.already_accrued && !row.block_reason,
      )
    },
    topupEligibleSelectedIds() {
      const eligible = new Set(
        this.topupEligibleRows.map(row => row.coach_id),
      )

      return (this.topupPreviewSelected || [])
        .filter(row => row && eligible.has(row.coach_id))
        .map(row => row.coach_id)
    },
    topupPreviewSelectedTotal() {
      const selected = new Set(this.topupEligibleSelectedIds)

      return (this.topupPreviewRows || []).reduce((sum, row) => {
        if (!selected.has(row.coach_id)) return sum

        return sum + (Number(row.cashback) || 0)
      }, 0)
    },
    periodsCoverage() {
      return this.computePeriodsCoverage(this.periodRows, this.periodsYear)
    },
    periodsCoverageOk() {
      return this.periodsCoverage.ok
    },
    periodsCoverageHint() {
      return this.periodsCoverage.hint
    },
  },
  watch: {
    '$route.params.section': function onRouteSectionWatch(to, from) {
      const changedSection = Boolean(from) && to !== from
      this.onRouteSectionChange({ showSwitchLoader: changedSection })
    },
  },
  async created() {
    await this.onRouteSectionChange({ showSwitchLoader: false })
  },
  methods: {
    toast(msg, color = 'primary') {
      this.snackbarText = msg
      this.snackbarColor = color
      this.snackbar = true
    },

    apiError(err, fallback = 'Ошибка запроса') {
      const d = (err && err.response && err.response.data) || err || {}
      const msg = d.error
        || d.message
        || (d.data && (d.data.error || d.data.message))
        || (err && err.message)
        || fallback

      this.toast(String(msg), 'error')
    },

    isApiOk(res) {
      if (!res) return false
      if (res.success === true) return true
      if (res.status === 'success') return true

      return false
    },

    extractApiError(res, fallback = 'Ошибка запроса') {
      if (!res) return fallback

      return res.error
        || res.message
        || (res.data && (res.data.error || res.data.message))
        || fallback
    },

    sportLabel(code) {
      return SPORT_LABELS[code] || code || '—'
    },

    formatNumber(value) {
      if (value == null || value === '') return '—'

      return String(value)
    },

    formatPercent(value) {
      if (value == null || value === '') return '—'
      const n = Number(value)
      if (!Number.isFinite(n)) return String(value)

      return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '')
    },

    formatMoney(value) {
      const n = parseMoney(value)
      if (n == null) return '—'

      return `${Math.round(n)}`.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ') + ' ₽'
    },

    formatHours(value) {
      if (value == null || value === '') return '—'
      const n = Number(value)
      if (!Number.isFinite(n)) return String(value)

      return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '')
    },

    formatDateTime(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return String(iso)
      const pad = v => String(v).padStart(2, '0')

      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },

    formatTimeRange(start, end) {
      const trim = t => (t || '').slice(0, 5)

      return `${trim(start) || '—'}–${trim(end) || '—'}`
    },

    rowTotalPercent(item) {
      const a = Number(item.contract_cashback_percent) || 0
      const b = Number(item.production_cashback_percent) || 0

      return a + b
    },

    normalizeHoursTo(item) {
      if (item.hours_to === '' || item.hours_to == null) {
        item.hours_to = null
      } else {
        item.hours_to = Number(item.hours_to)
      }
    },

    isPeriodLocked(item) {
      if (!item || item.id == null) return false
      const map = this.hasTopups || {}

      return !!(map[item.id] || map[String(item.id)])
    },

    async onRouteSectionChange({ showSwitchLoader = false } = {}) {
      if (showSwitchLoader) this.sectionSwitchLoading = true
      try {
        const s = this.section
        if (s === 'list') {
          await this.loadCoaches()
        } else if (s === 'topups') {
          await Promise.all([
            this.loadTopupsPeriodFilter(),
            this.loadTopups(),
          ])
        } else if (s === 'settings') {
          await this.loadStatuses()
        } else if (s === 'periods') {
          await this.loadPeriods()
        }
      } finally {
        this.sectionSwitchLoading = false
      }
    },

    // --- list ---
    async loadCoaches() {
      this.listLoading = true
      try {
        const res = await Api.getCoachCashbackCoaches()
        if (!this.isApiOk(res)) {
          this.toast(this.extractApiError(res, 'Не удалось загрузить тренеров'), 'error')

          return
        }
        const data = res.data || {}
        this.coachesRaw = data.results || []
      } catch (e) {
        this.apiError(e, 'Не удалось загрузить тренеров')
      } finally {
        this.listLoading = false
      }
    },

    async onContractToggle(item, enabled) {
      if (!item.has_profile) return

      const next = !!enabled
      const prev = !!item.has_contract
      if (next === prev) return

      const action = next ? 'включить контракт' : 'снять контракт'
      const ok = window.confirm(
        next
          ? `Включить контракт для «${item.full_name}» с датой ${todayIso()}?`
          : `Снять контракт у «${item.full_name}»? Дата контракта будет очищена.`,
      )

      if (!ok) {
        this.$set(item, '_contractRev', (item._contractRev || 0) + 1)

        return
      }

      const payload = {
        coach_id: item.coach_id,
        has_contract: next,
      }
      if (next) {
        payload.contract_date = todayIso()
      }

      this.contractSavingId = item.coach_id
      try {
        const res = await Api.setCoachCashbackContract(payload)
        if (!this.isApiOk(res)) {
          item.has_contract = prev
          this.toast(this.extractApiError(res, `Не удалось ${action}`), 'error')

          return
        }
        const data = res.data || {}
        item.has_contract = !!data.has_contract
        item.contract_date = data.contract_date || null

        const raw = this.coachesRaw.find(
          row => row.coach && row.coach.id === item.coach_id,
        )
        if (raw && raw.coach) {
          raw.coach.contract = item.has_contract
          raw.coach.contract_date = item.contract_date
        }

        this.toast(next ? 'Контракт включён' : 'Контракт снят', 'success')
      } catch (e) {
        item.has_contract = prev
        this.apiError(e, `Не удалось ${action}`)
      } finally {
        this.contractSavingId = null
      }
    },

    async openTrainingsDialog(item) {
      const periodId = item.period_id
      if (!item.has_profile || !periodId) {
        this.toast('Нет периода для просмотра тренировок', 'warning')

        return
      }

      this.trainingsDialog = true
      this.trainingsLoading = true
      this.trainings = []
      this.trainingsCoachName = item.full_name
      this.trainingsPeriodLabel = item.period
        ? `${item.period.name || ''} (${item.period.date_from} — ${item.period.date_to})`
        : (item.period_label || '')

      try {
        const res = await Api.getCoachCashbackTrainings({
          coach_id: item.coach_id,
          period_id: periodId,
        })
        if (!this.isApiOk(res)) {
          this.toast(this.extractApiError(res, 'Не удалось загрузить тренировки'), 'error')

          return
        }
        const data = res.data || {}
        this.trainings = data.trainings || []
        if (data.cashback_period) {
          const p = data.cashback_period
          this.trainingsPeriodLabel = `${p.name || ''} (${p.date_from} — ${p.date_to})`
        } else if (data.period) {
          const p = data.period
          this.trainingsPeriodLabel = typeof p === 'object'
            ? `${p.name || ''} (${p.date_from || ''} — ${p.date_to || ''})`
            : String(p)
        }
      } catch (e) {
        this.apiError(e, 'Не удалось загрузить тренировки')
      } finally {
        this.trainingsLoading = false
      }
    },

    closeTopupWizard(force = false) {
      if (!force && (this.topupSaving || this.topupPreviewLoading)) return
      this.topupDialog = false
      this.topupStep = 1
      this.topupPeriodId = null
      this.topupComment = ''
      this.topupPreviewRows = []
      this.topupPreviewSelected = []
      this.topupPreviewError = ''
    },

    async startTopupWizard() {
      this.topupDialog = true
      this.topupStep = 1
      this.topupPeriodId = null
      this.topupComment = ''
      this.topupPreviewRows = []
      this.topupPreviewSelected = []
      this.topupPreviewError = ''
      this.finishedPeriodItems = []
      this.topupPeriodsLoading = true

      try {
        const year = new Date().getFullYear()
        const years = [year]
        if (year - 1 >= 2026) {
          years.push(year - 1)
        }
        const today = todayIso()
        const byId = {}

        await Promise.all(years.map(async y => {
          const res = await Api.getCoachCashbackPeriods({ year: y })
          if (!this.isApiOk(res)) return
          const periods = ((res.data || {}).periods || [])
            .filter(p => p.date_to && p.date_to < today)
          periods.forEach(p => {
            byId[p.id] = p
          })
        }))

        this.finishedPeriodItems = Object.values(byId)
          .sort((a, b) => String(b.date_from).localeCompare(String(a.date_from)))
          .map(p => ({
            text: `${p.year} · ${p.name} (${p.date_from} — ${p.date_to})`,
            value: p.id,
            raw: p,
          }))
      } catch (e) {
        this.apiError(e, 'Не удалось загрузить периоды')
      } finally {
        this.topupPeriodsLoading = false
      }
    },

    async loadTopupPreview() {
      if (!this.topupPeriodId) return

      this.topupPreviewLoading = true
      this.topupPreviewError = ''
      this.topupPreviewRows = []
      this.topupPreviewSelected = []
      this.loader = true

      try {
        if (!this.coachesRaw.length) {
          await this.loadCoaches()
        }

        const contracted = (this.coachesRaw || [])
          .filter(row => row.coach && row.coach.contract)
          .map(row => row.coach)

        if (!contracted.length) {
          this.topupPreviewError = 'Нет тренеров с подписанным контрактом'
          this.topupStep = 2

          return
        }

        const periodMeta = (
          this.finishedPeriodItems.find(p => p.value === this.topupPeriodId) || {}
        ).raw || null

        const results = await Promise.all(contracted.map(async coach => {
          try {
            const res = await Api.getCoachCashbackSummary({
              coach_id: coach.id,
              period_id: this.topupPeriodId,
            })
            if (!this.isApiOk(res)) {
              return {
                has_profile: true,
                coach_id: coach.id,
                full_name: coach.full_name || '—',
                sport: coach.sport,
                sport_display: coach.sport_display || this.sportLabel(coach.sport),
                hours: null,
                status_name: null,
                percent: null,
                cashback: 0,
                credited_cashback: null,
                already_accrued: false,
                block_reason: this.extractApiError(res, 'ошибка расчёта'),
                period_id: this.topupPeriodId,
                period: periodMeta,
                period_label: this.topupPeriodLabel,
              }
            }

            const data = res.data || {}
            const summary = data.summary || {}
            const status = summary.status || null
            const credited = summary.credited_cashback
            const already = credited != null

            return {
              has_profile: true,
              coach_id: coach.id,
              full_name: coach.full_name
                || (data.coach && data.coach.full_name)
                || '—',
              sport: coach.sport,
              sport_display: coach.sport_display || this.sportLabel(coach.sport),
              hours: summary.hours,
              status_name: status ? status.status_name : null,
              percent: status ? status.total_percent : null,
              cashback: already ? 0 : (summary.cashback || 0),
              credited_cashback: credited,
              already_accrued: already,
              block_reason: already ? '' : '',
              period_id: this.topupPeriodId,
              period: summary.period || periodMeta,
              period_label: this.topupPeriodLabel,
            }
          } catch (e) {
            return {
              has_profile: true,
              coach_id: coach.id,
              full_name: coach.full_name || '—',
              sport: coach.sport,
              sport_display: coach.sport_display || this.sportLabel(coach.sport),
              hours: null,
              status_name: null,
              percent: null,
              cashback: 0,
              credited_cashback: null,
              already_accrued: false,
              block_reason: 'ошибка загрузки',
              period_id: this.topupPeriodId,
              period: periodMeta,
              period_label: this.topupPeriodLabel,
            }
          }
        }))

        results.sort((a, b) => String(a.full_name).localeCompare(String(b.full_name), 'ru'))
        this.topupPreviewRows = results
        this.topupPreviewSelected = results.filter(
          row => !row.already_accrued && !row.block_reason,
        )
        this.topupStep = 2
      } catch (e) {
        this.apiError(e, 'Не удалось подготовить превью начисления')
      } finally {
        this.topupPreviewLoading = false
        this.loader = false
      }
    },

    onTopupPreviewItemSelected({ item, value }) {
      if (value && item && (item.already_accrued || item.block_reason)) {
        this.$nextTick(() => {
          this.topupPreviewSelected = this.topupPreviewSelected.filter(
            row => row.coach_id !== item.coach_id,
          )
        })
      }
    },

    onTopupPreviewToggleAll({ items, value }) {
      if (!value) return
      this.$nextTick(() => {
        this.topupPreviewSelected = (items || []).filter(
          row => !row.already_accrued && !row.block_reason,
        )
      })
    },

    async loadTopupsPeriodFilter() {
      this.topupsPeriodsLoading = true
      try {
        const year = this.topupsFilters.year || new Date().getFullYear()
        const res = await Api.getCoachCashbackPeriods({ year })
        if (!this.isApiOk(res)) {
          this.topupsPeriodItems = []

          return
        }
        this.topupsPeriodItems = ((res.data || {}).periods || []).map(p => ({
          text: `${p.name} (${p.date_from} — ${p.date_to})`,
          value: p.id,
        }))
      } catch (e) {
        this.topupsPeriodItems = []
        this.apiError(e, 'Не удалось загрузить периоды')
      } finally {
        this.topupsPeriodsLoading = false
      }
    },

    async onTopupsYearChange() {
      this.topupsFilters.period_id = null
      this.topupsTableOptions.page = 1
      await this.loadTopupsPeriodFilter()
      await this.loadTopups()
    },

    async onTopupsOptions(options) {
      if (!options) return
      this.topupsTableOptions = {
        ...this.topupsTableOptions,
        ...options,
      }
      if (this.section === 'topups') {
        await this.loadTopups()
      }
    },

    async loadTopups() {
      this.topupsLoading = true
      try {
        const page = this.topupsTableOptions.page || 1
        const pageSize = this.topupsTableOptions.itemsPerPage || 20
        const params = {
          page,
          page_size: pageSize === -1 ? 100 : pageSize,
        }
        if (this.topupsFilters.year) params.year = this.topupsFilters.year
        if (this.topupsFilters.period_id) {
          params.period_id = this.topupsFilters.period_id
        }

        const res = await Api.getCoachCashbackTopups(params)
        if (!this.isApiOk(res)) {
          this.toast(this.extractApiError(res, 'Не удалось загрузить начисления'), 'error')

          return
        }
        const data = res.data || {}
        this.topupsRows = data.results || []
        this.topupsTotal = data.count || 0
      } catch (e) {
        this.apiError(e, 'Не удалось загрузить начисления')
      } finally {
        this.topupsLoading = false
      }
    },

    async openTopupDetail(item) {
      if (!item || !item.id) return
      this.topupDetailDialog = true
      this.topupDetailLoading = true
      this.topupDetail = null
      try {
        const res = await Api.getCoachCashbackTopupDetail(item.id)
        if (!this.isApiOk(res)) {
          this.toast(this.extractApiError(res, 'Не удалось загрузить начисление'), 'error')
          this.topupDetailDialog = false

          return
        }
        this.topupDetail = res.data || null
      } catch (e) {
        this.apiError(e, 'Не удалось загрузить начисление')
        this.topupDetailDialog = false
      } finally {
        this.topupDetailLoading = false
      }
    },

    async submitTopup() {
      if (!this.topupPeriodId || !this.topupEligibleSelectedIds.length) return

      this.topupSaving = true
      this.loader = true
      try {
        const payload = {
          coach_ids: this.topupEligibleSelectedIds.slice(),
          period_id: this.topupPeriodId,
        }
        if (this.topupComment && this.topupComment.trim()) {
          payload.comment = this.topupComment.trim()
        }

        const res = await Api.postCoachCashbackTopup(payload)
        if (!this.isApiOk(res)) {
          this.toast(this.extractApiError(res, 'Не удалось выполнить начисление'), 'error')

          return
        }

        const data = res.data || {}
        const created = (data.created || []).length
        const failed = (data.failed || []).length
        let msg = `Начисление выполнено: создано ${created}`
        if (failed) msg += `, ошибок ${failed}`
        this.toast(msg, failed ? 'warning' : 'success')
        this.closeTopupWizard(true)
        await this.loadTopups()
      } catch (e) {
        this.apiError(e, 'Не удалось выполнить начисление')
      } finally {
        this.topupSaving = false
        this.loader = false
      }
    },

    // --- settings ---
    async loadStatuses() {
      this.statusesLoading = true
      try {
        const res = await Api.getCoachCashbackStatuses()
        if (!this.isApiOk(res)) {
          this.toast(this.extractApiError(res, 'Не удалось загрузить статусы'), 'error')

          return
        }
        const data = res.data || {}
        this.statusRows = (data.statuses || []).map(s => ({
          id: s.id,
          sport: s.sport,
          name: s.name,
          hours_from: s.hours_from,
          hours_to: s.hours_to == null ? null : s.hours_to,
          contract_cashback_percent: s.contract_cashback_percent,
          production_cashback_percent: s.production_cashback_percent,
        }))
      } catch (e) {
        this.apiError(e, 'Не удалось загрузить статусы')
      } finally {
        this.statusesLoading = false
      }
    },

    addStatusRow() {
      this.statusRows.push({
        id: null,
        sport: 'BADMINTON',
        name: '',
        hours_from: 0,
        hours_to: null,
        contract_cashback_percent: '0',
        production_cashback_percent: '0',
      })
    },

    removeStatusRow(index) {
      this.statusRows.splice(index, 1)
    },

    async saveStatuses() {
      const rows = this.statusRows.map(s => {
        const row = {
          sport: s.sport,
          name: (s.name || '').trim(),
          hours_from: Number(s.hours_from),
          hours_to: s.hours_to === '' || s.hours_to == null ? null : Number(s.hours_to),
          contract_cashback_percent: String(s.contract_cashback_percent),
          production_cashback_percent: String(s.production_cashback_percent),
        }
        if (s.id != null) row.id = s.id

        return row
      })

      if (rows.some(r => !r.name)) {
        this.toast('Укажите название для всех статусов', 'warning')

        return
      }

      this.statusesSaving = true
      this.loader = true
      try {
        const res = await Api.putCoachCashbackStatuses({ statuses: rows })
        if (!this.isApiOk(res)) {
          this.toast(this.extractApiError(res, 'Не удалось сохранить статусы'), 'error')

          return
        }
        const data = res.data || {}
        this.statusRows = (data.statuses || rows).map(s => ({
          id: s.id,
          sport: s.sport,
          name: s.name,
          hours_from: s.hours_from,
          hours_to: s.hours_to == null ? null : s.hours_to,
          contract_cashback_percent: s.contract_cashback_percent,
          production_cashback_percent: s.production_cashback_percent,
        }))
        this.toast('Статусы сохранены', 'success')
      } catch (e) {
        this.apiError(e, 'Не удалось сохранить статусы')
      } finally {
        this.statusesSaving = false
        this.loader = false
      }
    },

    // --- periods ---
    async loadPeriods() {
      this.periodsLoading = true
      try {
        const res = await Api.getCoachCashbackPeriods({ year: this.periodsYear })
        if (!this.isApiOk(res)) {
          this.toast(this.extractApiError(res, 'Не удалось загрузить периоды'), 'error')

          return
        }
        const data = res.data || {}
        this.hasTopups = data.has_topups || {}
        this.periodRows = (data.periods || []).map(p => ({
          id: p.id,
          year: p.year,
          name: p.name,
          date_from: p.date_from,
          date_to: p.date_to,
        }))
      } catch (e) {
        this.apiError(e, 'Не удалось загрузить периоды')
      } finally {
        this.periodsLoading = false
      }
    },

    addPeriodRow() {
      this.periodRows.push({
        id: null,
        year: this.periodsYear,
        name: '',
        date_from: '',
        date_to: '',
      })
    },

    removePeriodRow(index) {
      const item = this.periodRows[index]
      if (item && this.isPeriodLocked(item)) {
        this.toast('Нельзя удалить период с начислениями', 'warning')

        return
      }
      this.periodRows.splice(index, 1)
    },

    computePeriodsCoverage(rows, year) {
      const valid = (rows || [])
        .filter(r => r.date_from && r.date_to)
        .slice()
        .sort((a, b) => String(a.date_from).localeCompare(String(b.date_from)))

      if (!valid.length) {
        return {
          ok: false,
          hint: `Нет периодов за ${year}. Ожидается покрытие 01.01–31.12.`,
        }
      }

      const yearStart = `${year}-01-01`
      const yearEnd = `${year}-12-31`
      const issues = []

      if (valid[0].date_from !== yearStart) {
        issues.push(`первый период должен начинаться с ${yearStart}`)
      }
      if (valid[valid.length - 1].date_to !== yearEnd) {
        issues.push(`последний период должен заканчиваться на ${yearEnd}`)
      }

      for (let i = 0; i < valid.length; i += 1) {
        const cur = valid[i]
        if (cur.date_from > cur.date_to) {
          issues.push(`«${cur.name || i + 1}»: date_from > date_to`)
        }
        if (i > 0) {
          const prev = valid[i - 1]
          const expected = this.addDaysIso(prev.date_to, 1)
          if (cur.date_from !== expected) {
            if (cur.date_from < expected) {
              issues.push(`перекрытие между «${prev.name || i}» и «${cur.name || i + 1}»`)
            } else {
              issues.push(`разрыв после ${prev.date_to} до ${cur.date_from}`)
            }
          }
        }
      }

      if (issues.length) {
        return { ok: false, hint: `Покрытие года неполное: ${issues.join('; ')}.` }
      }

      return {
        ok: true,
        hint: `Покрытие ${year}: 01.01–31.12 без разрывов (${valid.length} период(ов)).`,
      }
    },

    addDaysIso(isoDate, days) {
      const [y, m, d] = String(isoDate).split('-').map(Number)
      const dt = new Date(y, m - 1, d)
      dt.setDate(dt.getDate() + days)
      const yy = dt.getFullYear()
      const mm = String(dt.getMonth() + 1).padStart(2, '0')
      const dd = String(dt.getDate()).padStart(2, '0')

      return `${yy}-${mm}-${dd}`
    },

    async savePeriods() {
      const periods = this.periodRows.map(p => {
        const row = {
          name: (p.name || '').trim(),
          date_from: p.date_from,
          date_to: p.date_to,
        }
        if (p.id != null) row.id = p.id

        return row
      })

      if (periods.some(p => !p.name || !p.date_from || !p.date_to)) {
        this.toast('Заполните название и даты у всех периодов', 'warning')

        return
      }

      this.periodsSaving = true
      this.loader = true
      try {
        const res = await Api.putCoachCashbackPeriods({
          year: this.periodsYear,
          periods,
        })
        if (!this.isApiOk(res)) {
          this.toast(this.extractApiError(res, 'Не удалось сохранить периоды'), 'error')

          return
        }
        const data = res.data || {}
        this.hasTopups = data.has_topups || this.hasTopups
        this.periodRows = (data.periods || periods).map(p => ({
          id: p.id,
          year: p.year || this.periodsYear,
          name: p.name,
          date_from: p.date_from,
          date_to: p.date_to,
        }))
        this.toast('Периоды сохранены', 'success')
      } catch (e) {
        this.apiError(e, 'Не удалось сохранить периоды')
      } finally {
        this.periodsSaving = false
        this.loader = false
      }
    },
  },
}
</script>
